// Editor "Edición gráfica" — se abre desde una imagen generada (?id=&companyId=).
// Esta página SOLO carga la imagen base (mismo patrón que EditarImagen: query a
// `imagenes_generadas` → firma en el bucket `generadas`) y arma la escena inicial;
// el editor en sí vive en <EditorShell> (así se puede testear aislado, ver Lab).
//
// Persistencia: front-only. Al guardar (M6) se hereda el `variante_id` de la
// imagen origen (por eso viaja en `source`). No gasta créditos.

import { useEffect, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { leerVolverA } from '../../utils/volverA';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { EditorShell, type EditorSource } from './EditorShell';
import { useSidebar } from '../../context/SidebarContext';
import type { ImageLayer, Scene } from './scene/types';

function loadRatio(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(
        img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1,
      );
    img.onerror = () => resolve(1);
    img.src = url;
  });
}

export default function EdicionGrafica() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth } = useSidebar();
  const imageId = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();

  // Contexto de la pantalla de resultados (lo pasa GeneratedImagesInline al abrir
  // el editor). Si viene, al Guardar el editor vuelve ahí mostrando la editada
  // como la imagen principal. Si no (ej. entrada directa por URL), Guardar hace
  // navigate(-1).
  const returnState = location.state as
    | { resultsImageIds?: string[]; resultsCompanyId?: string; returnPath?: string; returnStateKey?: string }
    | null;
  const returnTo =
    returnState?.resultsImageIds && returnState.resultsImageIds.length > 0
      ? {
          imageIds: returnState.resultsImageIds.map(String),
          companyId: returnState.resultsCompanyId ?? companyId,
          // Al volver, reabrir el detalle de la imagen desde la que se abrió el editor.
          sourceImageId: imageId || undefined,
          // Ruta/clave de retorno (el historial vuelve a /redes-sociales/imagenes con
          // expandImageId). Sin `returnPath`, a donde dijo quien abrió el editor
          // (`volverA`, ADR-2026-09-16-volver-a-donde-vine): el Agente, por ejemplo.
          path: returnState.returnPath ?? leerVolverA(returnState)?.path,
          stateKey: returnState.returnStateKey,
        }
      : null;

  const [ready, setReady] = useState<{ scene: Scene; source: EditorSource } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!imageId || !companyId) {
        setError('Falta la información de la imagen.');
        setLoading(false);
        return;
      }
      try {
        setError(null);
        const { data: row } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name, variante_id, formato')
          .eq('id', imageId)
          .eq('id_company', companyId)
          .maybeSingle();
        const r = row as {
          file_name?: string;
          variante_id?: string | null;
          formato?: string | null;
        } | null;
        if (!r?.file_name) throw new Error('Imagen no disponible');

        const { data: signed } = await supabase.storage
          .from('generadas')
          .createSignedUrl(`${companyId}/full/${r.file_name}`, 3600);
        if (!signed?.signedUrl) throw new Error('No se pudo firmar la imagen');

        const url = signed.signedUrl;
        const ratio = await loadRatio(url);
        if (cancelled) return;

        const base: ImageLayer = {
          id: 'base',
          type: 'image',
          src: url,
          xPct: 50,
          yPct: 50,
          wPct: 100,
          hPct: 100,
          radiusPct: 0,
          naturalRatio: ratio,
        };
        setReady({
          scene: { bg: '#ffffff', ratio, layers: [base], selectedId: null },
          source: {
            varianteId: r.variante_id ?? null,
            formato: r.formato ?? null,
            companyId,
          },
        });
        setLoading(false);
      } catch (e) {
        console.error('Edición gráfica: error cargando la imagen', e);
        if (!cancelled) {
          setError('No se pudo cargar la imagen.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageId, companyId]);

  if (loading) {
    return (
      <div
        className="fixed top-0 right-0 left-0 md:left-[var(--eg-sb)] z-30 flex items-center justify-center bg-surface-bg"
        style={{ height: 'var(--app-height, 100dvh)', '--eg-sb': `${sidebarWidth}px` } as CSSProperties}
      >
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error || !ready) {
    return (
      <div
        className="fixed top-0 right-0 left-0 md:left-[var(--eg-sb)] z-30 flex flex-col items-center justify-center gap-3 text-center px-6 bg-surface-bg"
        style={{ height: 'var(--app-height, 100dvh)', '--eg-sb': `${sidebarWidth}px` } as CSSProperties}
      >
        <p className="text-[15px] text-neutral-600">{error ?? 'No se pudo cargar la imagen.'}</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-10 px-4 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium shadow-sm transition-colors"
        >
          Volver
        </button>
      </div>
    );
  }

  return <EditorShell initialScene={ready.scene} source={ready.source} returnTo={returnTo} />;
}
